/**
 * Life4Earth V2 — Community Settings · Role Bundle section.
 *
 * DEV-1215 iter 1. Community-facing surface for applying / un-applying a
 * Role Bundle. Lives in the Basics tab of CommunitySettingsModal, just
 * below the community name field (per locked placement decision
 * 2026-06-05).
 *
 * UX shape:
 *   * No bundle applied  → dropdown of available bundles + Apply button
 *                          (with inline confirm strip explaining the
 *                          all-or-nothing semantics from POLICY §5)
 *   * Bundle applied     → current bundle display + Change + Remove
 *                          - Change: re-opens the dropdown; Apply replaces
 *                            (silent upsert at backend per Phase 1 single-
 *                            bundle constraint)
 *                          - Remove: inline confirm strip → unapply
 *
 * Backend contract (from DEV-1213):
 *   GET    /api/role-bundles/available?communityGuid=
 *   GET    /api/community-role-bundle-assignment/{communityGuid}
 *   POST   /api/community-role-bundle-assignment/{communityGuid}/apply
 *   DELETE /api/community-role-bundle-assignment/{communityGuid}
 *
 * Auth:
 *   * Read (current + available): any logged-in user.
 *   * Apply / Unapply: V2 Owner / Admin on the community
 *     (CommunityAuth.IsUserCommunityAdmin server-side; we mirror with
 *     the modal's disabledAll prop which gates the write buttons).
 *
 * Per CLAUDE.md i18n rules — all user-facing strings via useAppTranslation
 * with the `community` namespace.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  applyRoleBundleToCommunity,
  fetchAvailableRoleBundles,
  fetchCommunityRoleBundleAssignment,
  unapplyCommunityRoleBundle,
  type CommunityRoleBundleAssignmentResponse,
  type RoleBundleSummaryResponse,
} from "../../api/roleBundles";
import { useAppTranslation } from "../../i18n/useAppTranslation";

interface Props {
  communityGuid: string;
  /**
   * Mirrors the modal's disabledAll. When true the user is read-only on
   * this community — the picker still renders the current bundle (so they
   * can see what's applied) but Apply / Change / Remove are hidden.
   */
  disabledAll: boolean;
}

export default function CommunityRoleBundleSection({
  communityGuid,
  disabledAll,
}: Props) {
  const { t } = useAppTranslation("community");

  const [current, setCurrent] = useState<CommunityRoleBundleAssignmentResponse | null>(null);
  const [available, setAvailable] = useState<RoleBundleSummaryResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dropdown / confirm state.
  // `pickerOpen` = the admin clicked Change (or no bundle is applied) and
  // is choosing one. When false and a bundle is applied, we show the
  // compact current-bundle display.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedGuid, setSelectedGuid] = useState<string>("");
  const [confirmingApply, setConfirmingApply] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Fetch on mount / community change ──────────────────────

  const loadAll = useCallback(async () => {
    if (!communityGuid) return;
    setLoading(true);
    setError(null);
    try {
      const [assignment, bundles] = await Promise.all([
        fetchCommunityRoleBundleAssignment(communityGuid),
        fetchAvailableRoleBundles(communityGuid),
      ]);
      setCurrent(assignment);
      setAvailable(bundles);
    } catch (err: any) {
      setError(err?.message || t("roleBundleSection.errorLoad"));
    } finally {
      setLoading(false);
    }
  }, [communityGuid, t]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // When picker opens (Change clicked, or no current assignment), default
  // the dropdown selection to the current bundle if any; otherwise empty.
  useEffect(() => {
    if (pickerOpen) {
      setSelectedGuid(current?.roleBundleGuid ?? "");
      setConfirmingApply(false);
    }
  }, [pickerOpen, current]);

  // If no bundle is applied, the picker is implicitly open.
  const effectivelyOpen = pickerOpen || !current;

  // ── Derived ────────────────────────────────────────────────

  const selectedBundle = useMemo<RoleBundleSummaryResponse | null>(() => {
    if (!selectedGuid) return null;
    return available.find((b) => b.roleBundleGuid === selectedGuid) ?? null;
  }, [selectedGuid, available]);

  const isChangeIntent = !!current
    && !!selectedGuid
    && selectedGuid.toUpperCase() !== current.roleBundleGuid.toUpperCase();
  const isInitialApplyIntent = !current && !!selectedGuid;
  const canApply = (isChangeIntent || isInitialApplyIntent) && !saving;

  // ── Actions ────────────────────────────────────────────────

  const handleApply = async () => {
    if (!canApply || !selectedBundle) return;
    setSaving(true);
    setError(null);
    try {
      await applyRoleBundleToCommunity(communityGuid, selectedBundle.roleBundleGuid);
      await loadAll();
      setConfirmingApply(false);
      setPickerOpen(false);
    } catch (err: any) {
      setError(err?.message || t("roleBundleSection.errorApply"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await unapplyCommunityRoleBundle(communityGuid);
      await loadAll();
      setConfirmingRemove(false);
    } catch (err: any) {
      setError(err?.message || t("roleBundleSection.errorRemove"));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div style={{ marginBlockEnd: 18 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--text-primary)",
          marginBlockEnd: 6,
        }}
      >
        {t("roleBundleSection.title")}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "var(--text-secondary)",
          marginBlockEnd: 10,
          lineHeight: 1.5,
        }}
      >
        {t("roleBundleSection.subtitle")}
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            background: "var(--color-error-bg, rgba(220, 60, 60, 0.1))",
            color: "var(--color-error)",
            border: "1px solid var(--color-error)",
            borderRadius: 4,
            fontSize: 13,
            marginBlockEnd: 10,
          }}
        >
          {error}
        </div>
      )}

      {loading && !current && available.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
          {t("roleBundleSection.loading")}
        </div>
      )}

      {/* Current bundle compact display — only when applied AND picker is closed. */}
      {current && !pickerOpen && (
        <div
          style={{
            padding: "10px 14px",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
              {current.bundleLabel}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--text-muted)",
                fontFamily: "monospace",
                marginBlockStart: 2,
              }}
            >
              {current.bundleKey}
            </div>
          </div>
          {!disabledAll && (
            <>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={saving}
                style={btnSecondaryStyle}
              >
                {t("roleBundleSection.actionChange")}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRemove(true)}
                disabled={saving}
                style={btnDangerStyle}
              >
                {t("roleBundleSection.actionRemove")}
              </button>
            </>
          )}
        </div>
      )}

      {/* Remove confirm modal (DEV-1215 hotfix 2026-06-05 — community-facing
          actions get a deliberate modal moment vs. the inline-strip pattern
          we use for sysadmin authoring in the Admin Console). */}
      {confirmingRemove && current && (
        <ConfirmModal
          title={t("roleBundleSection.actionRemove")}
          body={t("roleBundleSection.removeConfirmText", { label: current.bundleLabel })}
          confirmLabel={
            saving
              ? t("roleBundleSection.removingBundle")
              : t("roleBundleSection.actionRemove")
          }
          confirmStyle="danger"
          saving={saving}
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() => void handleRemove()}
        />
      )}

      {/* Picker — visible when no bundle applied OR Change clicked. */}
      {effectivelyOpen && !disabledAll && (
        <div
          style={{
            marginBlockStart: current && pickerOpen ? 12 : 0,
            padding: "12px 14px",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
          }}
        >
          {available.length === 0 && !loading && (
            <div style={{ fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic" }}>
              {t("roleBundleSection.noBundlesAvailable")}
            </div>
          )}

          {available.length > 0 && (
            <>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--text-secondary)",
                  marginBlockEnd: 6,
                }}
              >
                {t("roleBundleSection.pickerLabel")}
              </label>
              <select
                value={selectedGuid}
                onChange={(e) => {
                  setSelectedGuid(e.target.value);
                  setConfirmingApply(false);
                }}
                disabled={saving}
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                <option value="">{t("roleBundleSection.pickerPlaceholder")}</option>
                {available.map((b) => (
                  <option key={b.roleBundleGuid} value={b.roleBundleGuid}>
                    {b.label} ({t("roleBundleSection.rolesCount", { count: b.roleCount })})
                  </option>
                ))}
              </select>

              {/* Preview pane — locked iter 1 scope: label + description +
                  role count from the picker's own data. No extra fetch. */}
              {selectedBundle && (
                <div
                  style={{
                    marginBlockStart: 10,
                    padding: "8px 10px",
                    background: "var(--bg-surface)",
                    borderRadius: 4,
                    border: "1px solid var(--border-default)",
                  }}
                >
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBlockEnd: 4 }}>
                    {selectedBundle.description?.trim() || (
                      <em style={{ color: "var(--text-muted)" }}>
                        {t("roleBundleSection.noDescription")}
                      </em>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {t("roleBundleSection.rolesCount", { count: selectedBundle.roleCount })}
                    {" · "}
                    <span style={{ fontFamily: "monospace" }}>{selectedBundle.bundleKey}</span>
                  </div>
                </div>
              )}

              {/* Apply / Cancel row. */}
              <div
                style={{
                  marginBlockStart: 12,
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                }}
              >
                {current && (
                  <button
                    type="button"
                    onClick={() => {
                      setPickerOpen(false);
                      setConfirmingApply(false);
                    }}
                    disabled={saving}
                    style={btnSecondaryStyle}
                  >
                    {t("common.cancel")}
                  </button>
                )}
                {!confirmingApply && (
                  <button
                    type="button"
                    onClick={() => setConfirmingApply(true)}
                    disabled={!canApply}
                    style={btnPrimaryStyle}
                  >
                    {isChangeIntent
                      ? t("roleBundleSection.actionReplace")
                      : t("roleBundleSection.actionApply")}
                  </button>
                )}
              </div>

              {/* Apply confirm modal — see Remove modal note above. */}
              {confirmingApply && selectedBundle && (
                <ConfirmModal
                  title={
                    isChangeIntent
                      ? t("roleBundleSection.actionReplace")
                      : t("roleBundleSection.actionApply")
                  }
                  body={
                    isChangeIntent && current
                      ? t("roleBundleSection.applyChangeConfirmText", {
                          from: current.bundleLabel,
                          to: selectedBundle.label,
                          count: selectedBundle.roleCount,
                        })
                      : t("roleBundleSection.applyConfirmText", {
                          label: selectedBundle.label,
                          count: selectedBundle.roleCount,
                        })
                  }
                  confirmLabel={
                    saving
                      ? t("roleBundleSection.applyingBundle")
                      : isChangeIntent
                        ? t("roleBundleSection.actionReplace")
                        : t("roleBundleSection.actionApply")
                  }
                  confirmStyle="primary"
                  saving={saving}
                  onCancel={() => setConfirmingApply(false)}
                  onConfirm={() => void handleApply()}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Read-only state for non-admins when no bundle is applied. */}
      {!current && disabledAll && !loading && (
        <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
          {t("roleBundleSection.noneAppliedReadOnly")}
        </div>
      )}
    </div>
  );
}

// Inline styles — small, localized to this component. CSS module would
// be overkill for this iter; if the section grows we can extract.

const btnPrimaryStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "var(--accent-primary)",
  color: "#ffffff",
  border: "none",
  borderRadius: 4,
  fontSize: 13,
  cursor: "pointer",
};

const btnSecondaryStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "transparent",
  color: "var(--text-primary)",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 13,
  cursor: "pointer",
};

const btnDangerStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "transparent",
  color: "var(--color-error)",
  border: "1px solid var(--color-error)",
  borderRadius: 4,
  fontSize: 13,
  cursor: "pointer",
};

// ────────────────────────────────────────────────────────────
// ConfirmModal (DEV-1215 2026-06-05 hotfix)
// ────────────────────────────────────────────────────────────
//
// Small centered confirmation dialog. Replaces the inline-strip pattern
// previously used here — for community-facing actions (admin user, not
// sysadmin) we want a deliberate modal moment instead of an in-flow
// strip. The inline strip pattern stays appropriate for sysadmin
// authoring surfaces (AdminRoleRhythms, Bundles tab) where speed of
// iteration matters more than ceremony.
//
// Renders via fixed positioning + high z-index so it overlays the
// surrounding CommunitySettingsModal cleanly. Backdrop click cancels
// unless a save is in flight.

interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  confirmStyle: "primary" | "danger";
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  confirmStyle,
  saving,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const { t } = useAppTranslation("community");

  return (
    <>
      <div
        onClick={() => { if (!saving) onCancel(); }}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          // Platform scrim token (rgba(0,0,0,0.6)) — matches the rest of
          // the modal infrastructure in this codebase.
          background: "var(--bg-scrim)",
          zIndex: 10000,
        }}
      />
      <div
        role="dialog"
        aria-labelledby="role-bundle-confirm-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(90vw, 480px)",
          // IMPORTANT: --bg-surface is rgba(255,255,255,0.04) in the dark
          // theme — fully transparent in practice. The fix Skip caught on
          // 2026-06-05: use --bg-tertiary (#1e293b), which is the opaque
          // surface token the surrounding CommunitySettingsModal uses.
          background: "var(--bg-tertiary)",
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          padding: "20px 22px",
          zIndex: 10001,
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
        }}
      >
        <h2
          id="role-bundle-confirm-title"
          style={{
            margin: 0,
            marginBlockEnd: 12,
            fontSize: 16,
            fontWeight: 600,
            color: "var(--text-primary)",
          }}
        >
          {title}
        </h2>
        <p
          style={{
            margin: 0,
            marginBlockEnd: 18,
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--text-secondary)",
          }}
        >
          {body}
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={btnSecondaryStyle}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            style={confirmStyle === "danger" ? btnDangerStyle : btnPrimaryStyle}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}
